import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus } from "lucide-react";

import {
  getPilotRequests,
  updatePilotRequest,
  type PilotRequest,
  type PilotRequestStatus,
} from "@/api/platform";
import { PilotRequestDetails } from "@/components/platform/pilot-requests/PilotRequestDetails";
import { PilotRequestFilters } from "@/components/platform/pilot-requests/PilotRequestFilters";
import { PilotRequestList } from "@/components/platform/pilot-requests/PilotRequestList";
import { ProvisionChurchDrawer } from "@/components/platform/pilot-requests/ProvisionChurchDrawer";
import { PlatformPageHeader } from "@/components/platform/ui/PlatformPageHeader";
import { WorkspaceDetails } from "@/components/platform/workspace/WorkspaceDetails";
import { WorkspaceList } from "@/components/platform/workspace/WorkspaceList";
import { WorkspaceShell } from "@/components/platform/workspace/WorkspaceShell";

export function PilotRequestsPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<
    PilotRequestStatus | "ALL"
  >("ALL");

  const [selectedId, setSelectedId] = useState("");
  const [provisionRequestId, setProvisionRequestId] = useState<
    string | null
  >(null);

  const [isApproving, setIsApproving] = useState(false);
  const [approvalError, setApprovalError] = useState<
    string | null
  >(null);

  const pilotRequestsQuery = useQuery({
    queryKey: [
      "platform",
      "pilot-requests",
      {
        search: search.trim(),
        status,
      },
    ],
    queryFn: () =>
      getPilotRequests({
        search: search.trim() || undefined,
        status,
        offset: 0,
        limit: 50,
      }),
  });

  const requests = pilotRequestsQuery.data?.items ?? [];

  useEffect(() => {
    if (requests.length === 0) {
      setSelectedId("");
      return;
    }

    const selectedRequestStillExists = requests.some(
      (request) => request.id === selectedId,
    );

    if (!selectedRequestStillExists) {
      setSelectedId(requests[0].id);
    }
  }, [requests, selectedId]);

  const selectedRequest =
    requests.find((request) => request.id === selectedId) ??
    requests[0] ??
    null;

  const provisionRequest =
    requests.find(
      (request) => request.id === provisionRequestId,
    ) ?? null;

  async function refreshPlatformData() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["platform", "pilot-requests"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["platform-dashboard"],
      }),
    ]);
  }

  async function handleApprove(request: PilotRequest) {
    setIsApproving(true);
    setApprovalError(null);

    try {
      const approvedRequest = await updatePilotRequest(
        request.id,
        {
          status: "APPROVED",
        },
      );

      await refreshPlatformData();

      setSelectedId(approvedRequest.id);
      setProvisionRequestId(approvedRequest.id);
    } catch (error) {
      setApprovalError(
        error instanceof Error
          ? error.message
          : "The pilot request could not be approved.",
      );
    } finally {
      setIsApproving(false);
    }
  }

  async function handleProvisioningComplete() {
    setProvisionRequestId(null);
    await refreshPlatformData();
  }

  return (
    <div className="space-y-8">
      <PlatformPageHeader
        title="Pilot Requests"
        description="Review and provision churches joining the MinistryIQ pilot."
        secondaryAction={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        }
        action={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-500"
          >
            <Plus className="h-4 w-4" />
            Create Pilot Church
          </button>
        }
      />

      {approvalError && (
        <div
          role="alert"
          className="rounded-2xl border border-rose-200 bg-rose-50 p-5"
        >
          <p className="font-semibold text-rose-900">
            Pilot request could not be approved.
          </p>

          <p className="mt-2 text-sm text-rose-700">
            {approvalError}
          </p>
        </div>
      )}

      {pilotRequestsQuery.isError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6">
          <p className="font-semibold text-rose-900">
            Pilot requests could not be loaded.
          </p>

          <p className="mt-2 text-sm text-rose-700">
            {pilotRequestsQuery.error instanceof Error
              ? pilotRequestsQuery.error.message
              : "An unexpected error occurred."}
          </p>

          <button
            type="button"
            onClick={() => pilotRequestsQuery.refetch()}
            className="mt-4 rounded-xl border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
          >
            Try Again
          </button>
        </div>
      ) : (
        <WorkspaceShell
          list={
            <WorkspaceList
              title="Pilot Requests"
              description={
                pilotRequestsQuery.isLoading
                  ? "Loading requests..."
                  : `${pilotRequestsQuery.data?.total ?? 0} churches`
              }
              toolbar={
                <PilotRequestFilters
                  search={search}
                  status={status}
                  onSearchChange={setSearch}
                  onStatusChange={(value) =>
                    setStatus(
                      value as PilotRequestStatus | "ALL",
                    )
                  }
                />
              }
            >
              {pilotRequestsQuery.isLoading ? (
                <PilotRequestListSkeleton />
              ) : (
                <PilotRequestList
                  requests={requests}
                  selectedId={selectedRequest?.id ?? null}
                  onSelect={setSelectedId}
                />
              )}
            </WorkspaceList>
          }
          details={
            <WorkspaceDetails>
              {pilotRequestsQuery.isLoading ? (
                <PilotRequestDetailsSkeleton />
              ) : (
                <PilotRequestDetails
                  request={selectedRequest}
                  onApprove={handleApprove}
                />
              )}

              {isApproving && (
                <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-medium text-indigo-700">
                  Approving pilot request...
                </div>
              )}
            </WorkspaceDetails>
          }
        />
      )}

      <ProvisionChurchDrawer
        open={Boolean(provisionRequest)}
        request={provisionRequest}
        onClose={() => setProvisionRequestId(null)}
        onComplete={handleProvisioningComplete}
      />
    </div>
  );
}

function PilotRequestListSkeleton() {
  return (
    <div className="divide-y divide-slate-200">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="animate-pulse px-5 py-5"
        >
          <div className="h-4 w-3/4 rounded bg-slate-200" />
          <div className="mt-3 h-3 w-1/2 rounded bg-slate-100" />
          <div className="mt-2 h-3 w-1/3 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function PilotRequestDetailsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-6 w-1/2 rounded bg-slate-200" />
        <div className="mt-3 h-4 w-1/3 rounded bg-slate-100" />
      </div>

      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 p-6"
        >
          <div className="h-5 w-1/3 rounded bg-slate-200" />

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="h-12 rounded bg-slate-100" />
            <div className="h-12 rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}